import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ChevronDown, ChevronRight, GraduationCap } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { kbQueryKeys, listMyKbAssignments, type KbAssignment } from '@/services/knowledge-base.service';

function targetLink(item: KbAssignment): string {
  if (item.targetType === 'document') return `/knowledge/article/${item.targetId}`;
  if (item.targetType === 'chapter') return `/knowledge/chapter/${item.targetId}`;
  return '/knowledge';
}

export function MyTrainingPage() {
  const navigate = useNavigate();
  const [showCompleted, setShowCompleted] = useState(false);

  const assignmentsQuery = useQuery({
    queryKey: kbQueryKeys.myAssignments('all'),
    queryFn: () => listMyKbAssignments('all'),
  });

  const items = assignmentsQuery.data ?? [];
  const overdue = items.filter((i) => i.status === 'overdue');
  const pending = items.filter((i) => i.status === 'pending');
  const completed = items.filter((i) => i.status === 'acknowledged');

  return (
    <div className="p-6">
      <PageHeader title="My Training" subtitle="Documents and chapters assigned to you." />

      {assignmentsQuery.isLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">
          Loading your training...
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<CheckCircle2 className="h-8 w-8 text-emerald-500" />}
          title="You're all caught up"
          description="No outstanding training assignments. Check back later or browse the knowledge base."
          action={{ label: 'Browse Knowledge Base', onClick: () => navigate('/knowledge/library') }}
        />
      ) : (
        <div className="space-y-6">
          {overdue.length > 0 && (
            <Section title="Overdue" icon={<GraduationCap className="h-4 w-4 text-red-500" />}>
              {overdue.map((a) => (
                <AssignmentRow key={a.kbAssignmentId} assignment={a} link={targetLink(a)} variant="overdue" />
              ))}
            </Section>
          )}

          <Section title="Due" icon={<GraduationCap className="h-4 w-4 text-blue-500" />}>
            {pending.length === 0 ? (
              <li className="px-4 py-4 text-sm text-slate-500">Nothing pending.</li>
            ) : (
              pending.map((a) => (
                <AssignmentRow key={a.kbAssignmentId} assignment={a} link={targetLink(a)} variant="pending" />
              ))
            )}
          </Section>

          <CollapsibleSection
            title={`Completed (${completed.length})`}
            icon={<CheckCircle2 className="h-4 w-4 text-emerald-500" />}
            open={showCompleted}
            onToggle={() => setShowCompleted((v) => !v)}
          >
            {completed.length === 0 ? (
              <li className="px-4 py-4 text-sm text-slate-500">No completed training yet.</li>
            ) : (
              completed.map((a) => (
                <AssignmentRow key={a.kbAssignmentId} assignment={a} link={targetLink(a)} variant="done" />
              ))
            )}
          </CollapsibleSection>
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-2.5">
        {icon}
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      </div>
      <ul className="divide-y divide-slate-100">{children}</ul>
    </div>
  );
}

function CollapsibleSection({
  title,
  icon,
  open,
  onToggle,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 border-b border-slate-200 px-4 py-2.5 text-left hover:bg-slate-50"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-slate-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-400" />
        )}
        {icon}
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      </button>
      {open && <ul className="divide-y divide-slate-100">{children}</ul>}
    </div>
  );
}

function AssignmentRow({
  assignment,
  link,
  variant,
}: {
  assignment: KbAssignment;
  link: string;
  variant: 'overdue' | 'pending' | 'done';
}) {
  return (
    <li className="px-4 py-2.5">
      <Link to={link} className="-mx-4 flex items-center justify-between gap-3 px-4 hover:bg-slate-50">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-slate-900">
            {assignment.targetTitle || `Assignment #${assignment.kbAssignmentId}`}
          </div>
          <div className="text-xs text-slate-500">{assignment.targetType}</div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2 text-xs text-slate-600">
          {variant === 'done' && assignment.acknowledgedAt && (
            <Badge variant="success">
              Done {new Date(assignment.acknowledgedAt).toLocaleDateString()}
            </Badge>
          )}
          {variant !== 'done' && assignment.dueAt && (
            <Badge variant={variant === 'overdue' ? 'error' : 'warning'}>
              Due {new Date(assignment.dueAt).toLocaleDateString()}
            </Badge>
          )}
        </div>
      </Link>
    </li>
  );
}
